import { base64ToBytes, bytesToBase64 } from '@/services/crypto/base64';

export const IV_LENGTH_BYTES = 12;

export interface EncryptedPayload {
  ciphertext: string;
  iv: string;
}

function toBufferSource(bytes: Uint8Array): ArrayBuffer {
  const copy = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(copy).set(bytes);
  return copy;
}

export async function encryptBytes(
  key: CryptoKey,
  plaintext: Uint8Array,
): Promise<{ ciphertext: ArrayBuffer; iv: Uint8Array }> {
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH_BYTES));
  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: toBufferSource(iv) },
    key,
    toBufferSource(plaintext),
  );
  return { ciphertext, iv };
}

export async function decryptBytes(
  key: CryptoKey,
  ciphertext: ArrayBuffer,
  iv: Uint8Array,
): Promise<Uint8Array> {
  const buffer = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: toBufferSource(iv) },
    key,
    ciphertext,
  );
  return new Uint8Array(buffer);
}

export async function encryptString(key: CryptoKey, plaintext: string): Promise<EncryptedPayload> {
  const data = new TextEncoder().encode(plaintext);
  const { ciphertext, iv } = await encryptBytes(key, data);
  return {
    ciphertext: bytesToBase64(new Uint8Array(ciphertext)),
    iv: bytesToBase64(iv),
  };
}

export async function decryptString(key: CryptoKey, payload: EncryptedPayload): Promise<string> {
  const ciphertext = base64ToBytes(payload.ciphertext);
  const iv = base64ToBytes(payload.iv);
  const plaintext = await decryptBytes(key, toBufferSource(ciphertext), iv);
  return new TextDecoder().decode(plaintext);
}

export async function encryptJson<T>(key: CryptoKey, value: T): Promise<EncryptedPayload> {
  return encryptString(key, JSON.stringify(value));
}

export async function decryptJson<T>(key: CryptoKey, payload: EncryptedPayload): Promise<T> {
  const text = await decryptString(key, payload);
  return JSON.parse(text) as T;
}

export async function encryptBlob(
  key: CryptoKey,
  blob: Blob,
): Promise<{ ciphertext: Uint8Array; iv: Uint8Array }> {
  const buffer = await blob.arrayBuffer();
  const { ciphertext, iv } = await encryptBytes(key, new Uint8Array(buffer));
  return { ciphertext: new Uint8Array(ciphertext), iv };
}

export async function decryptToBlob(
  key: CryptoKey,
  ciphertext: Uint8Array,
  iv: Uint8Array,
  mimeType = 'application/octet-stream',
): Promise<Blob> {
  const plaintext = await decryptBytes(key, toBufferSource(ciphertext), iv);
  const arr = new Uint8Array(plaintext.byteLength);
  arr.set(plaintext);
  return new Blob([arr.buffer], { type: mimeType });
}
